import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Content,
  ContentAudience,
  ContentSubType,
  ContentType,
} from '@amzn/awscat-pattern-library-service-model';
import { TiptapOutput, TiptapOutputType, getTextWithEntityPlaceholders } from '@amzn/awscat-editor';
import { DateTime } from 'luxon';
import _ from 'lodash';

import { descriptionInputMaxLength, descriptionInputMinLength } from '../../api/UtilsApi';
import { convertContent } from '../../services/ContentConversion';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { usePendingFlushCoordinator } from '../../contexts/PendingFlushCoordinatorContext';
import {
  AudienceOption,
  applyAudienceChange,
  getAllowedAudienceOptions,
} from '../../services/AudienceChangeOperation';
import { ContentEditorService } from '../../services/ContentEditorService';
import { ContentValidationError } from '../../services/Errors';
import {
  setContentDescription,
  validateDescription,
} from '../../services/DescriptionFieldOperations';
import {
  MetadataField,
  getMetadataFields,
  setContentSubType,
  setMetadataField,
  validateMetadataField,
} from '../../services/MetadataFieldOperations';
import {
  getAuthorsByline,
  getLastPublicationDate,
  getPreviouslyPublishedAudiences,
  getVersionLabel,
  isPublished,
} from '../../model/LoadedDocument';
import { log } from '../../loggers/Logger';
import { useAudience } from '../v2-hooks/useAudience';
import { useContent } from '../v2-hooks/useContent';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';

/** How long the description field idles between keystrokes before staging. Short enough to feel responsive, long enough to avoid thrashing the (expensive) Tiptap → text conversion. */
const DESCRIPTION_DEBOUNCE_MS = 500;

/** Per-field validation errors surfaced to the view. Each property is `undefined` when valid. */
export interface MetadataFieldErrors {
  /** Error for the description field (out-of-bounds length). */
  readonly description: string | undefined;
  /** Error for the content-type field (e.g. conversion not yet supported). */
  readonly contentType: string | undefined;
  /** Error for the code-repo URL not matching the target audience. */
  readonly codeRepoAudience: string | undefined;
  /** Error for an outstanding AppSec/OSS code review blocking the target audience. */
  readonly codeReview: string | undefined;
  /** Errors keyed by template-metadata schema key. Absent entries indicate a valid field. */
  readonly templateFields: Readonly<Record<string, string>>;
}

/**
 * Presentation state and commands for the v2 "Metadata" tab. Surfaces
 * already-projected primitives (no raw `Content` exposure) so the View
 * does not need to know about the model layout.
 */
export interface MetadataTabViewModel {
  /** Plain-text description; bind directly to the Tiptap editor. */
  readonly description: string;
  /** Currently-selected content type. Drives which subtype/template the form shows. */
  readonly contentType: ContentType;
  /**
   * Pattern-only sub-type (e.g. `MigrationPattern`). `undefined` for
   * non-Pattern content or when no sub-type is selected yet.
   */
  readonly contentSubType: string | undefined;
  /**
   * Schema-driven fields to render in the Configuration section. Empty
   * when the content type has no template (e.g. some legacy types).
   */
  readonly metadataFields: readonly MetadataField[];
  /** Currently-selected audience tile. */
  readonly audience: ContentAudience;
  /**
   * Audience tiles to render, with their disabled state. A tile is disabled
   * when its audience is "lower" than one this document has already been
   * published to (only upgrades are allowed once published).
   */
  readonly audienceOptions: readonly AudienceOption[];
  /**
   * Notice to show beneath the audience tiles when at least one option is
   * disabled, e.g. "Only upgrading audience is allowed". `undefined` when
   * every audience is selectable.
   */
  readonly audienceUpgradeNotice: string | undefined;
  /** Workflow version label (e.g. `"3.0"`); `undefined` when no workflow exists yet. */
  readonly version: string | undefined;
  /** Pre-formatted "owner and co-authors" string for display next to the version. */
  readonly authorsByline: string;
  /** Last publication date for the document; `undefined` when never published. */
  readonly lastPublicationDate: DateTime | undefined;

  /** True when the audience tiles should be interactive. False for already-published documents. */
  readonly canEditAudience: boolean;

  /** Per-field validation messages. Each field is `undefined` when valid. */
  readonly fieldErrors: MetadataFieldErrors;

  /** Min/max character bounds for the description field, for the char-count indicator. */
  readonly descriptionLengthBounds: { readonly min: number; readonly max: number };
  /**
   * Latest character count of the description, refreshed when the
   * description debounce fires (NOT per keystroke — `getTextWithEntityPlaceholders` is too
   * expensive to call on every change). The indicator updates in lockstep
   * with staging.
   */
  readonly descriptionLength: number;
  /**
   * True between a keystroke and the next debounce flush — the View should
   * show a "counting…" placeholder instead of `descriptionLength`, which is
   * stale until the flush runs.
   */
  readonly isDescriptionCharCountPending: boolean;

  /**
   * Stage a new description after a short idle window
   * (`DESCRIPTION_DEBOUNCE_MS`). The expensive Tiptap → plain-text
   * conversion runs once when the debounce fires (not per keystroke).
   * The latest output replaces any in-flight pending one.
   */
  readonly changeDescription: (output: TiptapOutput) => void;
  /**
   * Target type the user has requested but not yet confirmed; the view
   * opens the confirmation modal whenever this is defined. Cleared by
   * {@link cancelContentTypeChange} or by a successful confirm.
   */
  readonly pendingContentTypeChange: ContentType | undefined;
  /**
   * Source type captured when {@link requestContentTypeChange} ran. The
   * view binds the confirmation modal's "from" label to this — reading
   * `contentType` directly would flip mid-conversion, since Part 1 stages
   * the converted content before Part 2's save returns. `undefined` when
   * no change is pending.
   */
  readonly contentTypeChangeSourceType: ContentType | undefined;
  /** True while a confirm is mid-flight (convert + save running). */
  readonly isContentTypeChangeInFlight: boolean;
  /** Capture the user's pick from the type Select. The view opens its modal off `pendingContentTypeChange`. */
  readonly requestContentTypeChange: (next: ContentType) => void;
  /** Drop the pending pick (modal dismissed). No-op while a confirm is in flight. */
  readonly cancelContentTypeChange: () => void;
  /**
   * Confirm the pending pick: convert + save. Two phases:
   *
   * - Part 1 (convert + stage). When `convert` throws, surfaces the error
   *   and stages nothing — the user can pick a different target.
   * - Part 2 ({@link ContentEditorService.save}). When save throws, the
   *   staged content stays buffered and {@link isContentTypeSavePending}
   *   flips to `true`. The user retries via {@link retryContentTypeSave};
   *   another confirm is a no-op while pending so Part 1 isn't re-run.
   */
  readonly confirmContentTypeChange: () => Promise<void>;
  /**
   * True when a content-type conversion was staged but its follow-up save
   * failed. The view should disable the type selector and surface a Retry
   * affordance bound to {@link retryContentTypeSave} until this clears.
   */
  readonly isContentTypeSavePending: boolean;
  /**
   * Re-run the save for a previously-failed confirm without re-running
   * the conversion. No-op when {@link isContentTypeSavePending} is
   * `false`.
   */
  readonly retryContentTypeSave: () => Promise<void>;
  /** Stage a new pattern sub-type. Pattern only. */
  readonly changeContentSubType: (value: ContentSubType) => void;
  /** Stage a new value for a schema-driven metadata field. */
  readonly changeMetadataField: (field: MetadataField, value: unknown) => void;
  /** Stage a new audience after running it through `applyAudienceChange`. Surfaces validation errors when the switch is rejected. */
  readonly changeAudience: (next: ContentAudience) => void;
}

/**
 * Builds the description-change command. The expensive `getTextWithEntityPlaceholders`
 * conversion runs inside the debounced flush — not on every keystroke —
 * and the debounce is cancelled on unmount so a fast nav-away can't
 * trigger React unmount warnings.
 */
function useChangeDescriptionCommand(
  content: Content | undefined,
  service: ContentEditorService,
  setDescriptionError: (msg: string | undefined) => void,
  setDescriptionLength: (length: number) => void,
  setIsCharCountPending: (pending: boolean) => void,
): (output: TiptapOutput) => void {
  const flushCoordinator = usePendingFlushCoordinator();

  // Latest Tiptap JSON the user typed; the debounced flush reads from this ref.
  const pendingJsonRef = useRef<TiptapOutputType | undefined>(undefined);

  // Capture the live content via a ref so the debounced flush always sees
  // the latest server snapshot — capturing `content` in the closure would
  // make the debounce point at a stale Content from when the timer started.
  const contentRef = useRef(content);
  contentRef.current = content;

  const flushDescriptionEdit = useCallback(() => {
    const json = pendingJsonRef.current;
    if (json === undefined) return;
    pendingJsonRef.current = undefined;

    // Convert Tiptap JSON to plain text once per idle window (it's expensive).
    const text = getTextWithEntityPlaceholders(json) ?? '';

    // Refresh the char-count indicator alongside staging — it's reusing the
    // same `getTextWithEntityPlaceholders` pass, so this stays cheap. Clearing the "pending" flag
    // here is what swaps the View back from "Counting characters…" to the
    // real count.
    setDescriptionLength(text.length);
    setIsCharCountPending(false);

    // When the new length is out of bounds, surface an error and skip staging.
    const error = validateDescription(text);
    if (error) {
      setDescriptionError(error);
      return;
    }

    // Otherwise, stage the updated content and clear any prior error.
    setDescriptionError(undefined);
    const live = contentRef.current;
    if (!live) return;
    service.stageEdit(setContentDescription(live, text));
  }, [service, setDescriptionError, setDescriptionLength, setIsCharCountPending]);

  const debouncedFlush = useMemo(
    () => _.debounce(flushDescriptionEdit, DESCRIPTION_DEBOUNCE_MS),
    [flushDescriptionEdit],
  );

  // When the host unmounts, flush any pending debounce so in-flight keystrokes
  // aren't dropped when the user navigates away mid-typing.
  useEffect(() => {
    return () => {
      debouncedFlush.flush();
    };
  }, [debouncedFlush]);

  // While mounted, let a browser-close drain stage the pending keystroke too.
  useEffect(() => {
    return flushCoordinator.registerFlusher(() => debouncedFlush.flush());
  }, [flushCoordinator, debouncedFlush]);

  return useCallback(
    (output: TiptapOutput) => {
      pendingJsonRef.current = output.json;
      // Mark the count as pending so the View hides the (now-stale) number
      // while we wait for the next flush.
      setIsCharCountPending(true);
      debouncedFlush();
    },
    [debouncedFlush, setIsCharCountPending],
  );
}

export function useMetadataTabViewModel(): MetadataTabViewModel | undefined {
  const service = useContentEditorService();
  const content = useContent();
  const audience = useAudience();
  const document = useLoadedDocument();

  // ----- error state -----
  const [descriptionError, setDescriptionError] = useState<string | undefined>(undefined);
  const [descriptionLength, setDescriptionLength] = useState<number>(
    () => (content?.contentMetadata.description ?? '').length,
  );
  const [isDescriptionCharCountPending, setIsDescriptionCharCountPending] =
    useState<boolean>(false);
  const [contentTypeError, setContentTypeError] = useState<string | undefined>(undefined);
  const [pendingContentTypeChange, setPendingContentTypeChange] = useState<ContentType | undefined>(
    undefined,
  );
  // Snapshot of the source type at request time. Read by the view so the
  // modal's "from" label survives Part 1's stageEdit (which flips
  // `content.contentMetadata.contentType` to the target before Part 2's
  // save resolves).
  const [contentTypeChangeSourceType, setContentTypeChangeSourceType] = useState<
    ContentType | undefined
  >(undefined);
  const [isContentTypeChangeInFlight, setIsContentTypeChangeInFlight] = useState<boolean>(false);
  // Set when the conversion (Part 1) succeeded but the follow-up save (Part 2)
  // threw. While true, `confirmContentTypeChange` is a no-op so Part 1 doesn't
  // re-run — the user must call `retryContentTypeSave` to flush the
  // already-staged content to the server.
  const [isContentTypeSavePending, setIsContentTypeSavePending] = useState<boolean>(false);
  const [codeRepoAudienceError, setCodeRepoAudienceError] = useState<string | undefined>(undefined);
  const [codeReviewError, setCodeReviewError] = useState<string | undefined>(undefined);
  const [templateFieldErrors, setTemplateFieldErrors] = useState<Record<string, string>>({});

  // Updates a single template-field error in the keyed map; clears the entry when `msg` is undefined.
  const updateTemplateFieldError = useCallback((key: string, msg: string | undefined) => {
    setTemplateFieldErrors((prev) => {
      if (msg === undefined) {
        if (!(key in prev)) return prev;
        const { [key]: _removed, ...rest } = prev;
        return rest;
      }
      if (prev[key] === msg) return prev;
      return { ...prev, [key]: msg };
    });
  }, []);

  // ----- projections from the live content + handle -----
  const description = content?.contentMetadata.description ?? '';
  const contentType = content?.contentMetadata.contentType;
  const contentSubType = content?.contentTypeMetadata?.contentSubType;
  const metadataFields = useMemo(() => (content ? getMetadataFields(content) : []), [content]);
  const audienceOptions = useMemo(
    () => (document ? getAllowedAudienceOptions(getPreviouslyPublishedAudiences(document)) : []),
    [document],
  );
  const audienceUpgradeNotice = audienceOptions.some((o) => o.isDisabled)
    ? 'Only upgrading the intended audience is allowed. A publication must be in draft state to change the intended audience.'
    : undefined;
  const version = document ? getVersionLabel(document) : undefined;
  const authorsByline = document ? getAuthorsByline(document) : '';
  const lastPublicationDate = document ? getLastPublicationDate(document) : undefined;
  const published = document ? isPublished(document) : false;

  // ----- commands -----
  const changeDescription = useChangeDescriptionCommand(
    content,
    service,
    setDescriptionError,
    setDescriptionLength,
    setIsDescriptionCharCountPending,
  );

  const changeMetadataField = useCallback(
    (field: MetadataField, value: unknown) => {
      // When the value fails validation, surface an error and skip staging.
      const error = validateMetadataField(field, value);
      if (error) {
        updateTemplateFieldError(field.key, error);
        return;
      }
      // Otherwise, stage the updated content and clear any prior error.
      updateTemplateFieldError(field.key, undefined);
      const current = service.content;
      if (!current) throw new Error('content is unexpectedly closed');
      service.stageEdit(setMetadataField(current, field, value));
    },
    [service, updateTemplateFieldError],
  );

  const changeContentSubType = useCallback(
    (value: ContentSubType) => {
      const current = service.content;
      if (!current) throw new Error('content is unexpectedly closed');
      service.stageEdit(setContentSubType(current, value));
    },
    [service],
  );

  const changeAudience = useCallback(
    (next: ContentAudience) => {
      const current = service.content;
      if (!current || !audience) throw new Error('content is unexpectedly closed');
      const result = applyAudienceChange(current, audience, next);

      // When validation rejects the switch, surface the messages and skip staging.
      if (result.kind === 'rejected') {
        setCodeRepoAudienceError(result.errors.find((e) => e.code === 'codeRepoAudience')?.message);
        setCodeReviewError(result.errors.find((e) => e.code === 'codeReview')?.message);
        return;
      }

      // Otherwise, stage the (possibly mutated) content + the new audience together.
      setCodeRepoAudienceError(undefined);
      setCodeReviewError(undefined);
      service.stageEdit(result.content, result.audience);
    },
    [audience, service],
  );

  const requestContentTypeChange = useCallback(
    (next: ContentType) => {
      // Picking the current type is a no-op — don't open the modal.
      if (next === contentType) return;
      // Clear any stale error from a previous failed conversion so the modal
      // doesn't reopen with an outdated message.
      setContentTypeError(undefined);
      // Snapshot the source type alongside the target so the modal's "from"
      // label doesn't flip when Part 1 stages the converted content.
      setContentTypeChangeSourceType(contentType);
      setPendingContentTypeChange(next);
    },
    [contentType],
  );

  const cancelContentTypeChange = useCallback(() => {
    // While the confirm is mid-flight, modal dismiss is a no-op so the user
    // can't yank the rug out from under an in-progress conversion.
    if (isContentTypeChangeInFlight) return;
    // While save is pending retry, the modal must stay open until the user
    // either retries successfully or reloads — see `confirmContentTypeChange`.
    if (isContentTypeSavePending) return;
    setPendingContentTypeChange(undefined);
    setContentTypeChangeSourceType(undefined);
  }, [isContentTypeChangeInFlight, isContentTypeSavePending]);

  const confirmContentTypeChange = useCallback(async () => {
    // When a previous save is still pending retry, ignore — the user must
    // resolve it via `retryContentTypeSave` so Part 1 doesn't run twice.
    if (isContentTypeSavePending) return;
    if (pendingContentTypeChange === undefined) return;

    const current = service.content;
    if (!current) throw new Error('content is unexpectedly closed');

    setIsContentTypeChangeInFlight(true);
    try {
      // Part 1 — convert + stage. Anything that throws here means nothing
      // was staged, so the user can pick a different target and try again.
      let result;
      try {
        result = convertContent(current, pendingContentTypeChange);
      } catch (error) {
        log.error({ err: error }, 'content type conversion failed');
        setContentTypeError(
          'Content type conversion failed. Please reload the page and try again.',
        );
        setPendingContentTypeChange(undefined);
        setContentTypeChangeSourceType(undefined);
        return;
      }

      // Comment conversion is currently broken — `result.commentConversions`
      // is intentionally not forwarded to InlineCommentManager. Inline
      // comments will be lost when the body is saved under the new
      // section structure. The modal warns the user before they get here.
      service.stageEdit(result.content);

      // Part 2 — flush to the server. On failure we keep the staged state
      // and mark the VM "pending retry" so the view can offer a Retry
      // button without re-running Part 1; the modal stays open.
      try {
        await service.save();
      } catch (error) {
        log.error({ err: error }, 'failed to save after changing content type');
        setIsContentTypeSavePending(true);
        setContentTypeError(
          error instanceof ContentValidationError
            ? error.message
            : 'Content type was converted, but saving failed. Please retry to keep your changes.',
        );
        return;
      }
      setIsContentTypeSavePending(false);
      setContentTypeError(undefined);
      // Clear stale field errors from the previous content type — field keys
      // may not exist on the new type.
      setDescriptionError(undefined);
      setTemplateFieldErrors({});
      setPendingContentTypeChange(undefined);
      setContentTypeChangeSourceType(undefined);
    } finally {
      setIsContentTypeChangeInFlight(false);
    }
  }, [service, isContentTypeSavePending, pendingContentTypeChange]);

  const retryContentTypeSave = useCallback(async () => {
    // When nothing was left over from a failed save, retry has nothing to
    // do — short-circuit so callers don't accidentally trigger an extra
    // server round-trip on every render.
    if (!isContentTypeSavePending) return;

    setIsContentTypeChangeInFlight(true);
    try {
      try {
        await service.save();
      } catch (error) {
        setContentTypeError(
          error instanceof ContentValidationError
            ? error.message
            : 'Content type was converted, but saving failed. Please retry to keep your changes.',
        );
        return;
      }
      setIsContentTypeSavePending(false);
      setContentTypeError(undefined);
      setPendingContentTypeChange(undefined);
      setContentTypeChangeSourceType(undefined);
    } finally {
      setIsContentTypeChangeInFlight(false);
    }
  }, [isContentTypeSavePending, service]);

  // ----- aggregate field errors for the View -----
  const fieldErrors: MetadataFieldErrors = useMemo(
    () => ({
      description: descriptionError,
      contentType: contentTypeError,
      codeRepoAudience: codeRepoAudienceError,
      codeReview: codeReviewError,
      templateFields: templateFieldErrors,
    }),
    [
      descriptionError,
      contentTypeError,
      codeRepoAudienceError,
      codeReviewError,
      templateFieldErrors,
    ],
  );

  // A parent-teardown render can transiently clear content/audience/handle
  // before this view unmounts; return undefined so the view renders null
  // instead of throwing.
  if (!content || !audience || !document) return undefined;

  return {
    description,
    contentType: content.contentMetadata.contentType,
    contentSubType,
    metadataFields,
    audience,
    audienceOptions,
    audienceUpgradeNotice,
    version,
    authorsByline,
    lastPublicationDate,
    canEditAudience: !published,
    fieldErrors,
    descriptionLengthBounds: {
      min: descriptionInputMinLength,
      max: descriptionInputMaxLength,
    },
    descriptionLength,
    isDescriptionCharCountPending,
    changeDescription,
    pendingContentTypeChange,
    contentTypeChangeSourceType,
    isContentTypeChangeInFlight,
    requestContentTypeChange,
    cancelContentTypeChange,
    confirmContentTypeChange,
    isContentTypeSavePending,
    retryContentTypeSave,
    changeContentSubType,
    changeMetadataField,
    changeAudience,
  };
}
